<template>
  <div>
    <div v-if="cartItem.loading">
      <CartItemContentLoader />
    </div>

    <div
      v-else
      class="cart__line-item line-item js-line-item"
      :class="{
        'cart__line-item--draggable' : orderType == 'offer' && !(isFirstItem && isLastItem) && $store.getters.changesAllowed,
        'line-item--alternative' : cartItem.is_alternative
      }"
      role="listitem"
    >
      <div
        v-if="orderType == 'offer' && !(isFirstItem && isLastItem) && $store.getters.changesAllowed"
        class="cart__line-item__controls"
      >
        <button
          :disabled="isFirstItem"
          class="cart__line-item__move cart__line-item__move--up"
          @click.prevent="moveUp"
        >
          <span class="screen-reader-text">Siirrä ylöspäin</span>
        </button>

        <button
          class="cart__line-item__move cart__line-item__move--drag js-handle"
          @click.prevent
        >
          <span class="screen-reader-text">Raahaa</span>
        </button>

        <button
          :disabled="isLastItem"
          class="cart__line-item__move cart__line-item__move--down"
          @click.prevent="moveDown"
        >
          <span class="screen-reader-text">Siirrä alaspäin</span>
        </button>
      </div>

      <div
        class="cart__column cart__column--product"
        :class="{'cart__column--product--offer' : orderType == 'offer'}"
      >
        <div class="line-item__image-container">
          <a
            v-if="cartItem && cartItem.image"
            :href="cartItem.link"
          >
            <img
              v-lazy="cartItem.image.productThumbnail"
              class="cart-item__image"
              :srcset="cartItem.image.productThumbnail + ' 1x, ' + cartItem.image.productThumbnailX2 + ' 2x'"
              alt=""
            >
          </a>
        </div>

        <div class="line-item__info">
          <div class="line-item__info__main">
            <p
              v-if="orderType == 'offer' && cartItem.position_id"
              class="line-item__info__text"
            >
              {{ cartItem.position_id }}
            </p>
            <h3 class="line-item__title">
              <a
                class="line-item__link"
                :href="cartItem.link"
              >
                <span
                  v-if="cartItem.is_modeo_plus"
                  class="modeo-plus"
                >Modeo+</span>
                {{ name }}
              </a>
            </h3>
            <p
              v-if="orderType === 'offer'"
              class="line-item__sku"
            >
              {{ cartItem.sku }}
            </p>
            <p
              v-if="cartItem.description"
              class="line-item__description"
            >
              {{ cartItem.description }}
            </p>
            <p
              v-for="attribute in cartItem.attributes"
              :key="attribute.key"
              class="line-item__detail"
              :class="{'line-item__detail--alt' : orderType != 'offer'}"
            >
              {{ attribute.name }}: {{ attribute.value }}
            </p>
          </div>

          <div
            v-if="cartItem.add_ons && cartItem.add_ons.length > 0"
            class="line-item__addons"
          >
            <button
              class="line-item__addons__toggle"
              :class="{'line-item__addons__toggle--active' : showAddons}"
              @click.prevent="toggleAddons"
            >
              {{ $modeoL10n.additional_options }}
              <span class="line-item__addons__sum"><price
                prefix="+"
                :price-without-tax="addOnTotal().price_without_tax"
                :price-with-tax="addOnTotal().price_with_tax"
              /></span>
            </button>
            <div
              v-if="showAddons"
              class="line-item__addons__content"
            >
              <p
                v-for="addOn in cartItem.add_ons"
                :key="addOn.key"
                class="line-item__addons__value"
              >
                {{ addOn.name }} <price
                  prefix="+"
                  :price-without-tax="addOn.prices_without_tax.price"
                  :price-with-tax="addOn.prices_with_tax.price"
                />
              </p>
            </div>
          </div>

          <div
            v-if="$store.getters.changesAllowed"
            class="line-item__actions"
          >
            <label
              v-if="orderType == 'offer'"
              class="line-item__button line-item__label"
            >
              <input
                v-model="cartItem.is_alternative"
                class="line-item__checkbox"
                type="checkbox"
                @change="handleChange()"
              >
              Vaihtoehtoinen tuote
            </label>
            <button
              v-if="orderType == 'offer'"
              class="line-item__button"
              @click.prevent="editCartItem()"
            >
              Muokkaa tuotetta
              <span class="screen-reader-text"> {{ cartItem.name }}</span>
            </button>
            <button
              class="line-item__button"
              @click.prevent="showConfirmaton()"
            >
              {{ $modeoL10n.remove_product }}
              <span class="screen-reader-text"> {{ cartItem.name }}</span>
            </button>
            <button
              v-if="orderType == 'offer'"
              class="line-item__button"
              @click.prevent="$emit('duplicate', cartItem)"
            >
              Kopioi
              <span class="screen-reader-text"> {{ cartItem.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="orderType == 'order'"
        class="cart__column cart__column--availability"
      >
        <span class="cart__column__label">{{ $modeoL10n.availability }}</span>

        <p
          class="availability  availability--small"
          :class="availability.status ? 'availability--' + availability.status : ''"
        >
          {{ availability.text }}
          <span
            v-if="availability.detail"
            class="availability__detail"
          >{{ availability.detail }}</span>
        </p>
      </div>

      <div
        v-if="orderType == 'offer'"
        class="cart__column cart__column--price cart__column--price-input"
      >
        <span class="cart__column__label">Kust.hinta</span>
        <span class="line-item__price">
          <span class="line-item__price__input-container">
            <!-- // these are just for offers and need to be price & cost_price -->
            <input
              v-model="cartItem.cost_price"
              class="line-item__price__input small"
              type="text"
              :disabled="!$store.getters.changesAllowed"
              @keypress="forceFloat()"
              @change="handleChange()"
            >
            <span class="line-item__price__unit">€</span>
          </span>
        </span>
      </div>

      <div class="cart__column cart__column--price cart__column--price-input">
        <span class="cart__column__label">{{ $modeoL10n.unit_price }}</span>
        <span
          v-if="orderType == 'order'"
          class="line-item__price"
        ><price
          :price-without-tax="unitLineTotal().unit_line_total_without_tax"
          :price-with-tax="unitLineTotal().unit_line_total_with_tax"
        /></span>
        <span
          v-if="orderType == 'offer'"
          class="line-item__price"
        >
          <span class="line-item__price__input-container">
            <!-- // these are just for offers and need to be price & cost_price -->
            <input
              v-model="cartItem.price"
              class="line-item__price__input small"
              type="text"
              :disabled="!$store.getters.changesAllowed"
              @keypress="forceFloat()"
              @change="handleChange()"
            >
            <span class="line-item__price__unit">€</span>
          </span>
        </span>
      </div>

      <div
        v-if="orderType =='offer'"
        class="cart__column cart__column--profit"
      >
        <span class="cart__column__label">Kate</span>
        <span class="line-item__price">{{ profit | formatPercent }} %</span>
      </div>

      <div class="cart__column cart__column--amount">
        <span class="cart__column__label">{{ $modeoL10n.amount }}</span>
        <product-counter
          ref="counter"
          :default-count="cartItem.quantity"
          :max-count="maxCount"
          :counter-mode="'small'"
          @update="changeQuantity($event)"
          @change="changeQuantity($event)"
        />
      </div>

      <div class="cart__column cart__column--sum cart__column--sum-value">
        <span class="cart__column__label">{{ $modeoL10n.total }}</span>
        <span class="line-item__sum"><price
          :price-without-tax="lineTotal().price_without_tax"
          :price-with-tax="lineTotal().price_with_tax"
        /></span>
        <span class="line-item__tax tax">{{ $modeoL10n.vat }} {{ $store.getters.displayVat }}</span>
      </div>

      <DeleteItemConfirmation
        :display="showConfirmation"
        :quantity="cartItem.quantity"
        @confirm="remove()"
        @cancel="showConfirmation = false"
      />
    </div>
  </div>
</template>

<script>
import ProductCounter from '../../components/product/product-counter.vue';
import DeleteItemConfirmation from '../../components/cart/delete-item-confirmation.vue';
import CartItemContentLoader from '../../components/cart/cart-item-content-loader.vue';
import axios from 'axios';
import { EventBus } from '../../event-bus.js';
import _ from 'lodash';

export default {
    components: {
        ProductCounter,
        DeleteItemConfirmation,
        CartItemContentLoader,
    },
    props: {
        originalCartItem: {
            type: Object,
            default: function () {
                return {}
            },
        },
        orderType: {
            type: String,
            default: '',
        },
        isLastItem: Boolean,
        isFirstItem: Boolean,
    },
    data : function () {
        return {
            cartItem: this.originalCartItem,
            showConfirmation: false,
            showAddons: false,
            outOfStockThreshold: 0, //TODO: From ENV
            someInStockThreshold: 10, //TODO: From ENV,
        }
    },

    computed: {

        name: function() {
            if (typeof this.cartItem.custom_name === 'undefined' || this.cartItem.custom_name === '') {
                if (typeof this.cartItem.brand_name === 'undefined' || this.cartItem.brand_name == null) {
                    return this.cartItem.name;
                }
                return this.cartItem.brand_name + ' ' + this.cartItem.name;
            }
            return this.cartItem.custom_name;
        },

        maxCount() {
            if (this.cartItem.manage_stock && this.cartItem.backorders == 'no') {
                return this.cartItem.stock;
            } else {
                return false;
            }
        },

        enoughInStock: function() {
            return (this.lowestStock - this.cartItem.quantity) >= this.outOfStockThreshold;
        },

        someInStock: function() {
            const leftInStock = this.lowestStock - this.cartItem.quantity;
            return leftInStock >= this.outOfStockThreshold && leftInStock < this.someInStockThreshold;
        },

        /*
         * Loops through main product and add-ons and returns lowest stock amount.
         */
        lowestStock: function() {
            let lowestStock = this.cartItem.stock;

            if (!_.isEmpty(this.cartItem.add_ons)) {
                for (var i = 0; i < this.cartItem.add_ons.length; i++) {
                    if (this.cartItem.add_ons[i].stock < lowestStock) {
                        lowestStock = this.cartItem.add_ons[i].stock;
                    }
                }
            }
            return lowestStock;
        },

        profit() {
            const price = this.parseCommaDecimalFormat(this.unitLineTotal().unit_line_total_without_tax);
            const costPrice = this.parseCommaDecimalFormat(this.cartItem.cost_price) + this.addOnTotal().price_without_tax;

            if (price < 1) {
                return 0;
            }

            return (price - costPrice) / price * 100;
        },

        availability() {
            if (this.cartItem.outlet && this.enoughInStock) {
                return {'status': 'positive', 'text': this.$modeoL10n.in_outlet, 'detail': false};
            } else if (this.enoughInStock) {
                return {'status': 'positive', 'text': this.$modeoL10n.in_stock, 'detail': false};
            } else {
                let availability =  {'status': 'warning', 'text': this.$modeoL10n.delivery_time, 'detail': this.cartItem.estimated_delivery_time_with_add_ons + ' ' + (this.cartItem.estimated_delivery_time_with_add_ons == 1 ? this.$modeoL10n.week : this.$modeoL10n.weeks)};
                if (!this.cartItem.estimated_delivery_time_with_add_ons) {
                    availability.detail = false;
                }
                return availability;
            }
        },
    },

    mounted: function() {
        // these are just for offers and need to be price & cost_price
        this.cartItem.price = this.toCommaDecimalFormat(this.cartItem.price);
        this.cartItem.cost_price = this.toCommaDecimalFormat(this.cartItem.cost_price);
        if (!this.cartItem.cost_price) {
            this.cartItem.cost_price = this.cartItem.price;
        }
    },

    methods: {
        showConfirmaton() {
            this.showConfirmation = true;
        },

        remove : function() {
            this.$emit('remove');
            this.showConfirmation = false;
        },

        handleChange : function() {
            this.cartItem.estimated_delivery_time_with_add_ons = false;
            this.dUpdate();
        },

        editCartItem: function() {
            this.$emit("editCartItem");
        },

        setCartItem: function(data) {
            const cartItem = data.cartItem;
            this.cartItem = cartItem;
            this.$refs.counter.setCount(cartItem.quantity);
        },

        getProductData() {
            return {
                "cart_item_key": this.cartItem.key,
                "add_ons": this.cartItem.add_ons,
                "quantity": parseInt( this.cartItem.quantity ),
                // these are just for offers and need to be price & cost_price
                "price": this.parseCommaDecimalFormat( this.cartItem.price ),
                "cost_price": this.parseCommaDecimalFormat( this.cartItem.cost_price ),
                "position_id": this.cartItem.position_id,
                "description": this.cartItem.description,
                "custom_name": this.cartItem.custom_name,
                "variation_id": this.cartItem.variation_id,
                "variation": this.cartItem.variation,
                "product_id": this.cartItem.product_id,
                "is_alternative": this.cartItem.is_alternative,
            };
        },

        update : function() {

            const request = this.getProductData();
            const config = {
                headers: {
                    'X-WP-Nonce': this.cartItem.nonce,
                },
            };

            axios.post('/wp-json/modeo/v2/cart/update_cart_item', request, config)
            .then(response => this.updateTotals(response))
            .catch((error) => {
                if ( error.response ) {

                    if ( error.response.status === 403 && error.response.data.code === 'Forbidden' ) {
                        let productAddedToCartData = {
                            message: this.$modeoL10n.offer_modification_failed,
                            notificationType: 'single-message',
                        };
                        EventBus.$emit( 'notify', productAddedToCartData );
                    }

                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    console.log( error.response.data );
                    console.log( error.response.status );
                    console.log( error.response.headers );
                } else {
                    // Something happened in setting up the request that triggered an Error
                    console.log( 'Error', error.message );
                }
                console.log( error.config );
            });
        },

        updateTotals(response) {
            const totals = {
                subtotal: response.data.subtotal,
                subtotalTax: response.data.subtotal_tax,
                total: response.data.total,
                discount: response.data.discount_total,
                discountTax: response.data.discount_tax,
                count: response.data.cart_product_count,
            };

            this.$store.commit( 'setTotals', totals );

            this.cartItem.estimated_delivery_time_with_add_ons = response.data.delivery_time;

            this.$emit( 'update', {
                'quantity': parseInt( this.cartItem.quantity ),
                'estimated_delivery_time_with_add_ons': response.data.delivery_time,
                'cost_price': this.cartItem.cost_price,
                'price': this.cartItem.price,
            } );
        },

        dUpdate: _.debounce(function() {
            this.update();
        }, 350),

        changeQuantity : function(quantity) {
            this.cartItem.estimated_delivery_time_with_add_ons = false;
            this.cartItem.quantity = quantity;
            this.dUpdate();
        },

        unitLineTotal : function() {

            let price_without_tax = this.cartItem.prices_without_tax.price;
            if ( this.orderType === 'offer' ) {
                // this is just for offers and need to be price
                price_without_tax = this.cartItem.price;
            }

            return {
                unit_line_total_without_tax: this.parseCommaDecimalFormat( price_without_tax ) + this.addOnTotal().price_without_tax,
                unit_line_total_with_tax: this.parseCommaDecimalFormat( this.cartItem.prices_with_tax.price ) + this.addOnTotal().price_with_tax,
            };
        },

        lineTotal : function() {
            if (this.cartItem.price === '') {
                return 0;
            }

            let price_without_tax = this.cartItem.prices_without_tax.price;
            if ( this.orderType === 'offer' ) {
                // this is just for offers and need to be price
                price_without_tax = this.cartItem.price;
            }

            const price_without_taxes = this.parseCommaDecimalFormat( price_without_tax ) + this.addOnTotal().price_without_tax;
            const price_with_taxes = this.parseCommaDecimalFormat( this.cartItem.prices_with_tax.price ) + this.addOnTotal().price_with_tax;

            return {
                price_without_tax: price_without_taxes * this.cartItem.quantity,
                price_with_tax: price_with_taxes * this.cartItem.quantity,
            }
        },

        addOnTotal() {
            if (!Array.isArray(this.cartItem.add_ons)) {
                return { price_without_tax: 0, price_with_tax: 0 };
            }

            let total_without_tax = 0;
            let total_with_tax = 0;
            for(let i=0; i < this.cartItem.add_ons.length; i++) {
                total_with_tax += this.parseCommaDecimalFormat(this.cartItem.add_ons[i].prices_with_tax.price);
                total_without_tax += this.parseCommaDecimalFormat(this.cartItem.add_ons[i].prices_without_tax.price);
            }
            return {
                price_without_tax: total_without_tax,
                price_with_tax: total_with_tax,
            };
        },

        toggleAddons() {
            this.showAddons = !this.showAddons;
        },

        /** Offers */
        moveUp() {
            this.$emit('moveUp', this.cartItem);
        },

        moveDown() {
            this.$emit('moveDown', this.cartItem);
        },
    },
}
</script>
